/**
 * ReglasCajaTab — Página «POS Clobi»: el instalador y el manual de la aplicación de punto de venta, y
 * lo que Clobi define para todas las cajas de la empresa (pedido del POS, 2026-10-30):
 * imprimir al emitir por tipo de documento, qué acciones piden además el PIN de un encargado y el
 * bloqueo de la sesión por inactividad. Baja a los terminales por `GET /terminal/` en menos de un minuto.
 *
 * La casilla de autorización se SUMA a las capacidades del rol (decisión de Gabriel): nunca le da a un
 * cajero algo que su rol no tiene; con la capacidad, decide si además lo aprueba otra persona.
 */
import { useEffect, useState } from 'react'
import { Save } from 'lucide-react'
import { usePermission } from '@/hooks/usePermission'
import { useToast, ToastContainer } from '@/components/Toast'
import { Button } from '@/components/Button'
import { CheckboxField } from '@/components/ds/CheckboxField'
import { TextField } from '@/components/ds/TextField'
import { extractErrorMessage } from '@/lib/errorUtils'
import { usePosCajaSettings, useSavePosCajaSettings, type PosCajaSettings } from '../hooks/usePos'
import { POS_CAPABILITIES } from '../capabilities'
import { nombreDelTipo } from '../tiposDeDocumento'
import InstaladorCard from './InstaladorCard'

/** Copia de `ACCIONES` y `TIPOS_IMPRIMIBLES` de `apps/pos/autorizaciones.py`. */
const ACCIONES = [
  'discount.line', 'discount.global', 'void.before_print', 'credit_note',
  'cash.withdraw', 'reprint', 'cash.close_other', 'terminal.configure',
]
const TIPOS = ['39', '33', '61']

function Seccion({ titulo, ayuda, children }: { titulo: string; ayuda: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-divider bg-surface p-4 sm:p-5">
      <h2 className="text-sm font-semibold text-content">{titulo}</h2>
      <p className="mt-1 text-xs text-content-muted">{ayuda}</p>
      <div className="mt-4">{children}</div>
    </section>
  )
}

export default function ReglasCajaTab() {
  const { can } = usePermission()
  const editable = can('pos.terminals', 'edit')
  const toast = useToast()
  const { data, isLoading } = usePosCajaSettings()
  const { mutateAsync: guardar, isPending } = useSavePosCajaSettings()
  const [borrador, setBorrador] = useState<PosCajaSettings | null>(null)
  const [minutos, setMinutos] = useState('')

  useEffect(() => {
    if (!data) return
    setBorrador(data)
    setMinutos(data.session_idle_timeout_minutes ? String(data.session_idle_timeout_minutes) : '')
  }, [data])

  if (isLoading || !borrador) return <p className="text-sm text-content-muted">Cargando…</p>

  const imprime = (t: string) => borrador.auto_print[t] ?? false
  const marcar = (campo: 'auto_print' | 'authorization_required', clave: string, valor: boolean) =>
    setBorrador({ ...borrador, [campo]: { ...borrador[campo], [clave]: valor } })

  const onGuardar = async () => {
    try {
      await guardar({
        // Al guardar, los tres tipos quedan definidos por Clobi: la casilla es obligatoria en la caja.
        auto_print: Object.fromEntries(TIPOS.map((t) => [t, imprime(t)])),
        authorization_required: borrador.authorization_required,
        session_idle_timeout_minutes: minutos.trim() === '' ? null : Number(minutos),
      })
      toast.success('Reglas guardadas. Las cajas las reciben en menos de un minuto.')
    } catch (e) {
      toast.error(extractErrorMessage(e, 'No se pudieron guardar las reglas.'))
    }
  }

  return (
    <div className="space-y-4">
      <InstaladorCard
        programa="pos"
        titulo="Instalar POS Clobi"
        pasos={[
          <>Crear el terminal en <strong>Terminales</strong>, clase Punto de venta, asignarle folios y dar de alta a sus <strong>Operadores</strong>.</>,
          'Ejecutar el instalador en el equipo de la caja (Windows 10 u 11 de 64 bits). No hace falta ser administrador del equipo.',
          <>Al abrirlo, ingresar la dirección de la empresa y el código de activación del terminal (ícono de llave en <strong>Terminales</strong>; vence a los 15 minutos).</>,
        ]}
        nota={<>Si Windows muestra «Windows protegió su PC», elegir «Más información» y «Ejecutar de todas formas».
          El manual trae los requisitos, la impresora, el uso diario de la caja y los mensajes frecuentes.</>}
        onError={(e) => toast.error(extractErrorMessage(e, 'No se pudo descargar el instalador.'))}
      />

      <div className="pt-2">
        <h2 className="text-base font-semibold text-content">Reglas de las cajas</h2>
        <p className="mt-1 text-sm text-content-muted">Valen para todas las cajas de la empresa y les llegan en menos de un minuto.</p>
      </div>

      <Seccion
        titulo="Imprimir al emitir"
        ayuda="Si la caja imprime el documento apenas lo emite. Es obligatorio en la caja: lo que no se imprime al emitir se reimprime a pedido."
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {TIPOS.map((t) => (
            <CheckboxField
              key={t}
              label={nombreDelTipo(Number(t))}
              checked={imprime(t)}
              disabled={!editable}
              onChange={(e) => marcar('auto_print', t, e.target.checked)}
            />
          ))}
        </div>
      </Seccion>

      <Seccion
        titulo="Requiere autorización de un encargado"
        ayuda="Marcada, la acción pide además el PIN de otra persona con «Autorizar en la caja», aunque el operador tenga la capacidad en su rol. Desmarcada, decide el rol. Quien no tiene la capacidad siempre necesita autorización."
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {ACCIONES.map((clave) => {
            const cap = POS_CAPABILITIES.find((c) => c.key === clave)
            return (
              <CheckboxField
                key={clave}
                label={cap?.label ?? clave}
                description={cap?.hint}
                checked={borrador.authorization_required[clave] ?? false}
                disabled={!editable}
                onChange={(e) => marcar('authorization_required', clave, e.target.checked)}
              />
            )
          })}
        </div>
      </Seccion>

      <Seccion
        titulo="Bloqueo por inactividad"
        ayuda="Minutos sin uso tras los que la caja pide de nuevo el PIN del mismo operador. No se bloquea durante un cobro, conserva la venta a medio armar y no cierra la caja. Vacío: sin bloqueo."
      >
        <div className="max-w-xs">
          <TextField
            label="Minutos (1 a 120)"
            inputProps={{ type: 'number', min: 1, max: 120, inputMode: 'numeric' }}
            value={minutos}
            disabled={!editable}
            placeholder="Sin bloqueo"
            onChange={(e) => setMinutos(e.target.value)}
          />
        </div>
      </Seccion>

      {editable && (
        <div className="flex justify-end">
          <Button onClick={onGuardar} isPending={isPending} pendingLabel="Guardando…" leftIcon={<Save size={14} />}>
            Guardar
          </Button>
        </div>
      )}
      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </div>
  )
}
